import { ConfirmAction, List } from "@atlas/composite";
import {
  Form,
  FormEditableField,
  FormSelectField,
  Label,
  useToast,
} from "@atlas/core";
import { useSessionData, useTranslation } from "@atlas/sdk";
import { IHubCatalogVisibility, IHubPackageVisibility } from "@enums";
import type { IHubCatalog, IHubPackage } from "@models";
import {
  useHubCatalogsByIds,
  useUpdateHubCatalog,
  useUpdateHubPackage,
} from "@query";
import {
  CatalogVisibilityUpgradeAction,
  getCatalogVisibilityUpgradeAction,
  getHubCatalogVisibilityBadge,
} from "@utils";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

export interface IEditablePackageVisibilityProps {
  pkg?: IHubPackage | null;
  fallbackVisibility?: IHubPackageVisibility;
}

export function EditablePackageVisibility({
  pkg,
  fallbackVisibility,
}: IEditablePackageVisibilityProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { userId } = useSessionData();
  const uid = (userId ?? "").trim();
  const { mutate: updateHubPackage } = useUpdateHubPackage();
  const { mutateAsync: updateHubCatalog } = useUpdateHubCatalog();

  const visibility = useMemo(() => {
    return (
      pkg?.visibility ?? fallbackVisibility ?? IHubPackageVisibility.Private
    );
  }, [pkg?.visibility, fallbackVisibility]);

  const visibilityForm = useForm({
    defaultValues: { visibility },
  });

  const [confirmState, setConfirmState] = useState<{
    newVisibility: IHubPackageVisibility;
    catalogsToReview: IHubCatalog[];
    catalogsToUpgradeCompany: IHubCatalog[];
  } | null>(null);

  const catalogIdsFromPackage = useMemo(() => {
    return (
      pkg?.catalogRelations
        ?.map((r) => ({
          catalogId: r.catalogId,
          version: r.catalogVersion,
        }))
        .filter(
          (item): item is { catalogId: number; version: number } =>
            item.catalogId != null &&
            item.catalogId > 0 &&
            item.version != null,
        ) ?? []
    );
  }, [pkg?.catalogRelations]);

  const { data: allCatalogs } = useHubCatalogsByIds(catalogIdsFromPackage, {
    enabled: catalogIdsFromPackage.length > 0,
  });

  useEffect(() => {
    visibilityForm.reset({ visibility });
  }, [visibility, visibilityForm]);

  const handleVisibilityChange = (newVisibility: IHubPackageVisibility) => {
    if (!pkg?.packageId) return;

    if (allCatalogs && allCatalogs.length > 0) {
      const catalogsToReview: IHubCatalog[] = [];
      const catalogsToUpgradeCompany: IHubCatalog[] = [];

      for (const catalog of allCatalogs) {
        const action = getCatalogVisibilityUpgradeAction(
          catalog.visibility,
          newVisibility,
        );
        if (action === CatalogVisibilityUpgradeAction.UpgradePlatformReview) {
          catalogsToReview.push(catalog);
        } else if (action === CatalogVisibilityUpgradeAction.UpgradeCompany) {
          catalogsToUpgradeCompany.push(catalog);
        }
      }

      if (catalogsToReview.length > 0 || catalogsToUpgradeCompany.length > 0) {
        setConfirmState({
          newVisibility,
          catalogsToReview,
          catalogsToUpgradeCompany,
        });
        return;
      }
    }

    updatePackageVisibility(newVisibility);
  };

  const updatePackageVisibility = (newVisibility: IHubPackageVisibility) => {
    if (!pkg?.packageId) return;
    const body: IHubPackage = {
      ...pkg,
      visibility: newVisibility,
    };
    updateHubPackage(body, {
      onSuccess: () => {
        toast.add({
          title: t("hub.package_manager.edit_success", {
            defaultValue: "Package updated successfully",
          }),
          data: { type: "success" },
        });
      },
      onError: () => {
        toast.add({
          title: t("hub.package_manager.edit_error", {
            defaultValue: "Could not update package",
          }),
          data: { type: "error" },
        });
        visibilityForm.reset({ visibility });
      },
    });
  };

  const handleConfirm = async () => {
    if (!confirmState) return;
    const { newVisibility, catalogsToReview, catalogsToUpgradeCompany } =
      confirmState;
    setConfirmState(null);

    try {
      if (catalogsToReview.length > 0) {
        if (!pkg) return;
        const body: IHubPackage = {
          ...pkg,
          visibility: newVisibility,
          catalogRelations: pkg.catalogRelations?.filter(
            (r) =>
              !catalogsToReview.some(
                (c) =>
                  c.catalogId === r.catalogId && c.version === r.catalogVersion,
              ),
          ),
        };
        updateHubPackage(body, {
          onSuccess: () => {
            toast.add({
              title: t("hub.package_manager.edit_success", {
                defaultValue: "Package updated successfully",
              }),
              data: { type: "success" },
            });
          },
          onError: () => {
            toast.add({
              title: t("hub.package_manager.edit_error", {
                defaultValue: "Could not update package",
              }),
              data: { type: "error" },
            });
            visibilityForm.reset({ visibility });
          },
        });
      } else if (catalogsToUpgradeCompany.length > 0) {
        await Promise.all(
          catalogsToUpgradeCompany.map((catalog) =>
            updateHubCatalog({
              ...catalog,
              visibility: IHubCatalogVisibility.Company,
            }),
          ),
        );
        updatePackageVisibility(newVisibility);
      }
    } catch {
      toast.add({
        title: t("hub.package_manager.edit_error", {
          defaultValue: "Could not update package or catalogs",
        }),
        data: { type: "error" },
      });
      visibilityForm.reset({ visibility });
    }
  };

  const handleCancelConfirm = () => {
    setConfirmState(null);
    visibilityForm.reset({ visibility });
  };

  const currentVisibility = visibilityForm.watch("visibility");

  return (
    <>
      <Form {...visibilityForm}>
        <form>
          <FormEditableField
            name="visibility"
            readOnly={pkg?.createdBy !== uid}
            label={t("hub.checkout.package_visibility", {
              defaultValue: "Package Visibility",
            })}
            value={
              getHubCatalogVisibilityBadge(
                currentVisibility as unknown as IHubCatalogVisibility,
                t,
              ) || currentVisibility
            }>
            <FormSelectField
              control={visibilityForm.control}
              name="visibility"
              label={t("hub.checkout.package_visibility", {
                defaultValue: "Package Visibility",
              })}
              required
              description={t("hub.checkout.package_visibility_desc", {
                defaultValue: "Select who can see this package.",
              })}
              items={[
                {
                  value: IHubPackageVisibility.Private,
                  label: t("hub.package_manager.visibility_private", {
                    defaultValue: "Private",
                  }),
                  description: t(
                    "hub.package_manager.visibility_private_desc",
                    {
                      defaultValue: "Only visible to you",
                    },
                  ),
                },
                {
                  value: IHubPackageVisibility.Company,
                  label: t("hub.package_manager.visibility_company", {
                    defaultValue: "Company",
                  }),
                  description: t(
                    "hub.package_manager.visibility_company_desc",
                    {
                      defaultValue: "Visible to anyone in your company",
                    },
                  ),
                },
                {
                  value: IHubPackageVisibility.Platform,
                  label: t("hub.package_manager.visibility_platform", {
                    defaultValue: "Platform",
                  }),
                  description: t(
                    "hub.package_manager.visibility_platform_desc",
                    {
                      defaultValue: "Visible to all platform users",
                    },
                  ),
                },
              ]}
              disabled={!pkg?.packageId}
              onValueChange={(val) => {
                if (!val) return;
                handleVisibilityChange(val as IHubPackageVisibility);
              }}
            />
          </FormEditableField>
        </form>
      </Form>
      <ConfirmAction
        open={confirmState != null}
        onOpenChange={(open) => !open && handleCancelConfirm()}
        title={
          confirmState?.catalogsToReview.length
            ? t("hub.checkout.upgrade_platform_confirm_title_remove", {
                defaultValue: "Remove catalogs and update visibility?",
              })
            : t("hub.checkout.upgrade_confirm_title", {
                defaultValue: "Update catalog visibility?",
              })
        }
        description={
          confirmState?.catalogsToReview.length ? (
            <div className="flex flex-col gap-3">
              <Label className="font-normal text-sm">
                {t("hub.checkout.upgrade_platform_package_description_remove", {
                  count: confirmState.catalogsToReview.length,
                  defaultValue:
                    "To update the package visibility, {{count}} catalog(s) will be removed from the package as they require a visibility upgrade. Do you want to proceed?",
                })}
              </Label>
              <div className="max-h-40 overflow-y-auto rounded-md border bg-muted/50 p-3">
                <List gap={4} minItemWidth={200}>
                  {confirmState.catalogsToReview.map((c) => (
                    <div
                      key={`${c.catalogId}-${c.version}`}
                      className="flex items-center gap-2">
                      <div className="size-1.5 rounded-full bg-foreground/50" />
                      <Label className="font-normal text-sm">
                        {c.label || c.name}
                      </Label>
                    </div>
                  ))}
                </List>
              </div>
            </div>
          ) : (
            t("hub.checkout.upgrade_company_package_description", {
              defaultValue:
                "To change the package visibility to Company, some catalogs will also be changed to Company visibility. Do you want to proceed?",
            })
          )
        }
        confirmText={
          confirmState?.catalogsToReview.length
            ? t("hub.checkout.remove_and_update", {
                defaultValue: "Remove and Update",
              })
            : t("hub.checkout.upgrade", {
                defaultValue: "Update",
              })
        }
        cancelText={t("hub.administer.cancel", {
          defaultValue: "Cancel",
        })}
        actionVariant={
          confirmState?.catalogsToReview.length ? "destructive" : "default"
        }
        onConfirm={handleConfirm}
      />
    </>
  );
}
